import * as React from "react";

/* ──────────────────────────────────────────────────────────────────────────────
   Contact Modal Context
   ------------------------------------------------------------------------------
   Purpose:
   - Provide global open/close state for the Contact modal.
   - Avoid prop-drilling through Layout / Header / Pages.
   - Expose a clean hook: useContactModal()
   ────────────────────────────────────────────────────────────────────────────── */

/* 
  We initialise with `undefined` instead of `null` so we can
  properly detect misuse (hook used outside provider).
*/
const ContactModalContext = React.createContext(undefined);

/* ──────────────────────────────────────────────────────────────
   Provider
   - Wrap your app (already done in layout.js)
   - Controls modal open/close state
   ────────────────────────────────────────────────────────────── */
export function ContactModalProvider({ children }) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Stable open/close handlers (won’t re-create on each render)
  const openContact = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeContact = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  /*
    Memoise context value so consumers only re-render
    when `isOpen` actually changes.
  */
  const value = React.useMemo(
    () => ({
      isOpen,
      openContact,
      closeContact,
    }),
    [isOpen, openContact, closeContact]
  );

  return (
    <ContactModalContext.Provider value={value}>
      {children}
    </ContactModalContext.Provider>
  );
}

/* ──────────────────────────────────────────────────────────────
   Hook
   - Ensures clean usage inside components.
   - Throws a helpful error if used outside Provider.
   ────────────────────────────────────────────────────────────── */
export function useContactModal() {
  const context = React.useContext(ContactModalContext);

  if (context === undefined) {
    throw new Error(
      "useContactModal must be used within a ContactModalProvider"
    );
  }

  return context;
}
